import { useCallback, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { cn } from '@repo/videa-components/src/lib/utils';
import { Button } from '@repo/videa-components/src/ui/button';
import { Input } from '@repo/videa-components/src/ui/input';

import {
  usePreferences,
  usePreferencesPracticeUpdate,
} from '@/react-query/usePreferences';

const AGE_LIMIT_MIN = 1;
const AGE_LIMIT_MAX = 100;
const AGE_LIMIT_DEFAULT = 20;

const NOTIFICATION_TIMEOUT = 2000;

export const FlourideTaskAgeInput = () => {
  const { data: prefs } = usePreferences();
  const [tempValue, setTempValue] = useState<number>(
    prefs?.fluorideTaskAgeLimit ?? AGE_LIMIT_DEFAULT,
  );
  const [showSuccess, setShowSuccess] = useState(false);

  const isOutOfRange = tempValue < AGE_LIMIT_MIN || tempValue > AGE_LIMIT_MAX;

  const preferencesUpdate = usePreferencesPracticeUpdate({
    onSuccess: useCallback(() => {
      setShowSuccess(true);
      const timer = setTimeout(() => {
        setShowSuccess(false);
      }, NOTIFICATION_TIMEOUT);
      return () => clearTimeout(timer);
    }, []),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempValue(parseInt(e.target.value));
  };

  const handleSave = useCallback(() => {
    if (!isOutOfRange) {
      preferencesUpdate.mutate({
        fluorideTaskAgeLimit: tempValue,
      });
    }
  }, [isOutOfRange, tempValue, preferencesUpdate]);

  return (
    <div className="vai-flex vai-flex-col vai-gap-1 vai-ml-12 vai-w-full">
      <div className="vai-flex vai-items-center vai-gap-2">
        <label htmlFor="age-limit" className="vai-flex vai-items-center">
          <Trans>Show this task up to age:</Trans>
        </label>
        <div className="vai-flex vai-items-center vai-gap-2">
          <Input
            id="age-limit"
            type="number"
            min={AGE_LIMIT_MIN}
            max={AGE_LIMIT_MAX}
            value={tempValue}
            onChange={handleChange}
            aria-invalid={isOutOfRange}
            className={cn(
              'vai-w-24 [&:focus-visible]:vai-ring-0 [&:focus-visible]:vai-ring-offset-0 [&:focus-visible]:vai-outline-none',
              isOutOfRange && 'vai-border-red-500',
            )}
            disabled={preferencesUpdate.isLoading}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            className="vai-h-8"
            disabled={isOutOfRange || preferencesUpdate.isLoading}
          >
            {preferencesUpdate.isLoading ? (
              <Trans>Saving...</Trans>
            ) : (
              <Trans>Save</Trans>
            )}
          </Button>
        </div>
      </div>
      {isOutOfRange && (
        <div className="vai-text-red-500 vai-text-sm" role="alert">
          <Trans>
            Please enter a number between {AGE_LIMIT_MIN} and {AGE_LIMIT_MAX}
          </Trans>
        </div>
      )}
      {showSuccess && (
        <div className="vai-text-green-500 vai-text-sm" role="status">
          <Trans>Successfully updated!</Trans>
        </div>
      )}
    </div>
  );
};
